// Шапка черновика раздела (ui-plan п. 2): «беседа», «принять», «отменить»,
// «версии» с откатом. Стоит над предпросмотром черновика и над разделом,
// у которого есть черновик (метка «черновик» во вкладке).

import { useState } from 'react'
import { useAppStore } from '../../store/appStore'
import type { Section } from '../../store/appStore'
import { Menu } from '../../ui/Popover'
import type { MenuItem } from '../../ui/Popover'
import { acceptDraft, discardDraft, rollbackTo } from './builder'
import { describe } from './http'
import { readChats, sectionsApi, useSections } from './store'
import type { VersionRow } from './types'
import './custom.css'

function fmtVersion(v: VersionRow): string {
  const d = new Date(v.createdAt)
  const when = Number.isNaN(d.getTime())
    ? v.version
    : d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' })
  return `${when} · «${v.title}»`
}

export function VersionsButton({ id, className = 'abx-draftbar__btn' }: { id: string; className?: string }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [rows, setRows] = useState<VersionRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const open = async (el: HTMLElement) => {
    setAnchor(el)
    setRows(null)
    try {
      setRows((await sectionsApi.versions(id)).versions)
    } catch (e) {
      setError(describe(e))
      setRows([])
    }
  }
  const items: MenuItem[] = rows === null
    ? [{ id: 'wait', label: 'загрузка…', disabled: true }]
    : rows.length === 0
      ? [{ id: 'none', label: error ?? 'версий ещё нет — появятся после «принять»', disabled: true }]
      : rows.map((v) => ({
        id: v.version,
        label: fmtVersion(v),
        hint: v.current ? 'сейчас' : v.note.startsWith('откат') ? 'откат · вернуть' : 'откатить',
        active: v.current,
        disabled: v.current,
        onSelect: () => {
          void rollbackTo(id, v.version).catch((e) => useAppStore.getState().setNotice(`откат: ${describe(e)}`))
        },
      }))
  return (
    <>
      <button className={className} onClick={(e) => void open(e.currentTarget)} title="опубликованные версии раздела — откат">
        версии
      </button>
      <Menu open={!!anchor} anchor={anchor} onClose={() => setAnchor(null)} placement="bottom-end" width={300} items={items} />
    </>
  )
}

/** id — раздел на сервере; `preview` — мы в предпросмотре черновика. */
export function DraftBar({ id, title, preview, note }: {
  id: string; title: string; preview: boolean
  /** ограничение, которое оператор должен видеть до «принять» (оверлей встроенного) */
  note?: string | null
}) {
  const summary = useSections((s) => s.list.find((x) => x.id === id))
  const drafts = useAppStore((s) => s.drafts)
  const secEdit = useAppStore((s) => s.secEdit)
  const focusSession = useAppStore((s) => s.focusSession)
  const setSection = useAppStore((s) => s.setSection)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const chat = drafts.find((d) => d.id === `draft:${id}`)?.sessionId
    ?? Object.entries(secEdit).find(([k]) => k === id || k === `custom:${id}`)?.[1]
    ?? readChats()[id]
  const hasDraft = !!summary?.hasDraft
  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(describe(e))
    } finally {
      setBusy(false)
    }
  }
  const published = summary?.published || summary?.builtin
  return (
    <div className="abx-draftbar">
      <span className="abx-draftbar__tag">черновик</span>
      <span className="abx-draftbar__title">{title}</span>
      {chat ? <button className="abx-draftbar__btn" onClick={() => focusSession(chat)}>беседа</button> : null}
      {!preview && hasDraft ? (
        <button className="abx-draftbar__btn" onClick={() => setSection(`draft:${id}` as Section)}>предпросмотр</button>
      ) : null}
      {preview && published ? (
        <button className="abx-draftbar__btn" onClick={() => setSection((summary?.builtin ? id : `custom:${id}`) as Section)}>
          как сейчас
        </button>
      ) : null}
      {published ? <VersionsButton id={id} /> : null}
      <button className="abx-draftbar__btn is-quiet" disabled={busy} onClick={() => void run(() => discardDraft(id))}>
        отменить
      </button>
      <button className="abx-draftbar__btn is-ok" disabled={busy || !hasDraft} onClick={() => void run(() => acceptDraft(id))}
        title={hasDraft ? 'опубликовать черновик' : 'агент ещё не записал черновик'}>
        принять
      </button>
      {note ? <span className="abx-draftbar__note">{note}</span> : null}
      {error ? <span className="abx-draftbar__err">{error}</span> : null}
    </div>
  )
}
